import { apiClient } from './../client';
import type { IApiResponse } from './../types';
import type {
    IAddOnCheckoutInput,
    IAddOnPurchaseInput,
    IAddOnPurchaseResult,
    IBillingAddOnAutoRefill,
    IBillingAddOnsResponse,
    IBillingAutoRefillUpdateInput,
    IBillingCreditTransaction,
    IBillingInvoicesQuery,
    IBillingInvoicesResponse,
    IBillingPagedResponse,
    IBillingPlansResponse,
    IBillingSnapshot,
    IBillingTransactionsQuery,
    IBillingUsageDailyQuery,
    IBillingUsageDailyResponse,
    IBillingPortalInput,
    IBillingPortalSession,
    ISubscriptionActionResult,
    IBillingResourceLimit,
    IBillingUsageHistoryItem,
    IBillingUsageHistoryQuery,
    IBillingUsageSummary,
    ICheckoutSession,
    ICreditsCheckoutInput,
    ISubscriptionCheckoutInput,
} from '@/features/billing/types';

const BASE = '/billing';

export const billingApi = {
    /** LOCAL snapshot — renders even when the billing runtime is down. */
    getSubscription: () =>
        apiClient
            .get<IApiResponse<IBillingSnapshot>>(`${BASE}/subscription`)
            .then((r) => r.data.data),

    getPlans: () =>
        apiClient
            .get<IApiResponse<IBillingPlansResponse>>(`${BASE}/plans`)
            .then((r) => r.data.data),

    /** LIVE usage summary — may 503 when the billing runtime is unavailable. */
    getUsage: () =>
        apiClient.get<IApiResponse<IBillingUsageSummary>>(`${BASE}/usage`).then((r) => r.data.data),

    /** Slim per-resource limits — readable by any authenticated member (no billing:read). */
    getLimits: () =>
        apiClient
            .get<IApiResponse<readonly IBillingResourceLimit[]>>(`${BASE}/limits`)
            .then((r) => r.data.data),

    getUsageHistory: (params?: IBillingUsageHistoryQuery) =>
        apiClient
            .get<
                IApiResponse<IBillingPagedResponse<IBillingUsageHistoryItem>>
            >(`${BASE}/usage/history`, { params })
            .then((r) => r.data.data),

    getCreditTransactions: (params?: IBillingTransactionsQuery) =>
        apiClient
            .get<
                IApiResponse<IBillingPagedResponse<IBillingCreditTransaction>>
            >(`${BASE}/credits/transactions`, { params })
            .then((r) => r.data.data),

    getAddOns: () =>
        apiClient
            .get<IApiResponse<IBillingAddOnsResponse>>(`${BASE}/add-ons`)
            .then((r) => r.data.data),

    /** LIVE Stripe invoice list — cursor-paged (nextCursor), not page numbers. */
    getInvoices: (params?: IBillingInvoicesQuery) =>
        apiClient
            .get<IApiResponse<IBillingInvoicesResponse>>(`${BASE}/invoices`, { params })
            .then((r) => r.data.data),

    /** Zero-filled per-day consumption series for the usage trend chart. */
    getUsageDaily: (params?: IBillingUsageDailyQuery) =>
        apiClient
            .get<IApiResponse<IBillingUsageDailyResponse>>(`${BASE}/usage/daily`, { params })
            .then((r) => r.data.data),

    createSubscriptionCheckout: (data: ISubscriptionCheckoutInput) =>
        apiClient
            .post<IApiResponse<ICheckoutSession>>(`${BASE}/subscription/checkout`, data)
            .then((r) => r.data.data),

    createAddOnCheckout: ({ addOnKey, ...data }: IAddOnCheckoutInput) =>
        apiClient
            .post<
                IApiResponse<ICheckoutSession>
            >(`${BASE}/add-ons/${encodeURIComponent(addOnKey)}/checkout`, data)
            .then((r) => r.data.data),

    createCreditsCheckout: (data: ICreditsCheckoutInput) =>
        apiClient
            .post<IApiResponse<ICheckoutSession>>(`${BASE}/credits/checkout`, data)
            .then((r) => r.data.data),

    /** Opens the Stripe-hosted billing portal (manage plan / card / invoices). */
    createPortalSession: (data: IBillingPortalInput) =>
        apiClient
            .post<IApiResponse<IBillingPortalSession>>(`${BASE}/portal`, data)
            .then((r) => r.data.data),

    /** Off-session add-on charge on the saved card (no redirect). */
    purchaseAddOn: ({ addOnKey, ...data }: IAddOnPurchaseInput) =>
        apiClient
            .post<
                IApiResponse<IAddOnPurchaseResult>
            >(`${BASE}/add-ons/${encodeURIComponent(addOnKey)}/purchase`, data)
            .then((r) => r.data.data),

    /** Manage the threshold auto-refill policy for a purchased add-on. */
    updateAutoRefill: ({ addOnKey, ...data }: IBillingAutoRefillUpdateInput) =>
        apiClient
            .post<
                IApiResponse<IBillingAddOnAutoRefill>
            >(`${BASE}/add-ons/${encodeURIComponent(addOnKey)}/auto-refill`, data)
            .then((r) => r.data.data),

    /** Schedule cancellation at period end (stays active until then). */
    cancelSubscription: () =>
        apiClient
            .post<IApiResponse<ISubscriptionActionResult>>(`${BASE}/subscription/cancel`)
            .then((r) => r.data.data),

    /** Undo a pending cancellation before the period ends. */
    reactivateSubscription: () =>
        apiClient
            .post<IApiResponse<ISubscriptionActionResult>>(`${BASE}/subscription/reactivate`)
            .then((r) => r.data.data),
};
